/* eslint-disable max-len */
import { Icon } from "@iconify/react";
import { Dialog } from "@mui/material";
import { FormikConfig, FormikProps, useFormik } from "formik";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import * as Yup from "yup";
import _, { set } from "lodash";
import { format } from "date-fns";
import FileInput from "../../components/shared/FileInput";
import { Input, Label, TextArea } from "../../components/shared/InputField";
import { SwitchButton } from "../../components/shared/SwitchButton";
import Tooltip from "../../components/shared/Tooltip";

import {
  Box,
  ButtonPrimary,
  ButtonPrimary1,
  HStack,
  SettingHeader,
  SettingsRow,
  VStack,
  isEsopViewer,
} from "../../components/utils";
import {
  useDeleteDefaultSignature,
  useESginSettings,
  useGetGrantLetterSettings,
  useSetGrantLetterSettings,
} from "../../queries/siteSettings";

import { usePermissionStore } from "../../store/permissionStore";
import { GrantLetterSetting } from "../../types/SiteSettings";
import { downloadBlobObject } from "../../utils/DownloadFile";
import { determineUserAccessToResource } from "../../utils/auth";
import convertToBase64 from "../../utils/convertToBase64";
import {
  Action as DefaultAction,
  Resource,
} from "../../utils/interfaces/Companies";
import { Select } from "../../components/shared/Select";
import ImageResizer from "../../components/shared/ImageResizer";
import { grantHeadersObj } from "../../types/ExportExcelGrant";

export interface FileTypeModel {
  fileBlob: string;
  fileName?: string;
  fileType?: string;
}
interface DateFormatSelectorProps {
  initialOrder: string[];
  separator?: string;
  onChange: (order: string[]) => void;
}

type SubSettingsProps = {
  formik: FormikProps<GrantLetterSetting>;
};
type SettingsRowProps = {
  label: string;
  description?: string;
  value: boolean;
};

function GrantLetterSettings({ openCategory }: { openCategory?: string }) {
  const {
    data: _grantLetterSettingDetails,
    refetch,
    isError,
    isFetched,
  } = useGetGrantLetterSettings();
  const { data: eSignSettingDetails } = useESginSettings();

  const initialValues: GrantLetterSetting = {
    acceptanceNumber: _grantLetterSettingDetails?.acceptanceNumber || 0,
    grantLetterTemplates:
      _grantLetterSettingDetails?.grantLetterTemplates || [],
    employeeSignatureForGrant:
      _grantLetterSettingDetails?.employeeSignatureForGrant || false,
    requestApproverSignature:
      _grantLetterSettingDetails?.requestApproverSignature || false,
    overrideGrantLetter:
      _grantLetterSettingDetails?.overrideGrantLetter || false,
    authorizedSignature: _grantLetterSettingDetails?.authorizedSignature,
    enableAutoExerciseRequest:
      _grantLetterSettingDetails?.enableAutoExerciseRequest ?? true,
    dateForLapsing:
      _grantLetterSettingDetails?.dateForLapsing || "lastWorkingDay",
    autoPauseVestingOnResignation:
      _grantLetterSettingDetails?.autoPauseVestingOnResignation || false,
    discardOfferedGrantsOnResignation:
      _grantLetterSettingDetails?.discardOfferedGrantsOnResignation || false,
    generatePlanDocument:
      _grantLetterSettingDetails?.generatePlanDocument || false,
    showZeroVestingInGrantLetter:
      _grantLetterSettingDetails?.showZeroVestingInGrantLetter || false,
    showValueAtGrantToAdmin:
      _grantLetterSettingDetails?.showValueAtGrantToAdmin || false,
    excludeWithdrawnFromTotal:
      _grantLetterSettingDetails?.excludeWithdrawnFromTotal || false,
    showActiveGrantedOptionsToAdmin:
      _grantLetterSettingDetails?.showActiveGrantedOptionsToAdmin || false,
    disableAutoGrantLetterGeneration:
      _grantLetterSettingDetails?.disableAutoGrantLetterGeneration || false,
    showGrantAcceptanceDisclaimer:
      _grantLetterSettingDetails?.showGrantAcceptanceDisclaimer || false,
    grantAcceptanceDisclaimerText:
      _grantLetterSettingDetails?.grantAcceptanceDisclaimerText || "",
  };
  const grantLetterSettingDetails = useMemo(
    () => _grantLetterSettingDetails,
    [_grantLetterSettingDetails]
  );
  const initialSettingsSet = useRef(false);
  const [initialSettings, setInitialSettings] = useState<GrantLetterSetting>(
    grantLetterSettingDetails!
  );
  const lapsingDateOptions = [
    { label: "Resignation Date", value: "resignationDate" },
    { label: "Last working Day", value: "lastWorkingDay" },
  ];
  const [separator, setSeparator] = useState("/");
  useEffect(() => {
    if (grantLetterSettingDetails) {
      formik.setValues({ ...initialValues, ...grantLetterSettingDetails });
    }
    if (!initialSettingsSet.current) {
      setInitialSettings(_.cloneDeep(grantLetterSettingDetails!));
      initialSettingsSet.current = true;
    }
  }, [grantLetterSettingDetails]);

  const { permission } = usePermissionStore();

  const validationSchema = Yup.object({
    acceptanceNumber: Yup.number()
      .positive("required")
      .typeError("expected a number"),
    employeeSignatureForGrant: Yup.boolean(),
    requestApproverSignature: Yup.boolean(),
    showGrantAcceptanceDisclaimer: Yup.boolean(),
    grantAcceptanceDisclaimerText: Yup.string().when(
      "showGrantAcceptanceDisclaimer",
      {
        is: true,
        then: (schema) =>
          schema
            .trim("Disclaimer text is required")
            .required("Disclaimer text is required"),
      }
    ),
  });

  const isUserEsopViewer = isEsopViewer();
  const { mutate: SetGrantLetterSettings, isLoading: isSettingsSaved } =
    useSetGrantLetterSettings();
  function doPartialTouch() {
    formik.setFieldTouched("acceptanceNumber");
  }

  const handleSubmit = (values: GrantLetterSetting) => {
    if (
      values.employeeSignatureForGrant &&
      eSignSettingDetails?.isDigioEnabled
    ) {
      toast(
        "Employee Signature for Grant Letter and E-Sign for Employees cannot be enabled at the same time",
        { type: "error", autoClose: 2000 }
      );
      return;
    }
    const changedGrantLetters = values.grantLetterTemplates?.filter(
      (template) => template.isChanged || template.isDeleted || template.isAdded
    );
    const changedValues: any = {};
    Object.keys(values).forEach((key) => {
      const originalKey: keyof GrantLetterSetting =
        key as keyof GrantLetterSetting;
      if (
        values[originalKey] !== initialValues[originalKey] &&
        key !== "grantLetterTemplates"
      ) {
        changedValues[originalKey] = values[originalKey];
      }
    });
    if (changedGrantLetters?.length) {
      changedValues.grantLetterTemplates = changedGrantLetters;
    }
    if (changedValues?.authorizedSignature) {
      changedValues.authorizedSignature = changedValues.authorizedSignature
        ?.toString()
        .replace("data:", "")
        .replace(/^.+,/, "");
    }
    if (Object.keys(changedValues).length === 0) {
      toast("No changes made", {
        type: "error",
        autoClose: 2000,
      });
      return;
    }
    SetGrantLetterSettings(changedValues, {
      onSuccess: () => {
        toast("Saved Successfully!", {
          type: "success",
          autoClose: 2000,
        });
        setTimeout(() => {
          refetch();
        }, 3000);
      },
      onError: (err: any) => {
        const errorPlanId = err.response?.data?.errorsValidation?.data?.planId;
        const index = formik.values.grantLetterTemplates.findIndex(
          (t) => t.esopPlanId === errorPlanId
        );
        toast(err.response?.data?.errorRaw || "Error occurred", {
          type: "error",
          autoClose: 2000,
        });
        if (errorPlanId) {
          formik.setFieldValue(
            `grantLetterTemplates[${index}].planLevelGrantTemplate`,
            ""
          );
          formik.setFieldValue(
            `grantLetterTemplates[${index}].isChanged`,
            false
          );
          formik.setFieldValue(`grantLetterTemplates[${index}].isAdded`, false);
          formik.setFieldValue(
            `grantLetterTemplates[${index}].isDeleted`,
            false
          );
        }
      },
    });
  };

  function handleOnCancel(values: GrantLetterSetting) {
    values = initialValues;
  }
  const separators = [
    { label: "Slash", value: "/" },
    { label: "Dash", value: "-" },
    { label: "Period", value: "." },
    { label: "Comma", value: "," },
    { label: "Blank", value: " " },
  ];

  const formik = useFormik<GrantLetterSetting>({
    initialValues,
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,

    onSubmit: (values) => {
      handleSubmit(values);
    },
    onReset: (values) => {
      handleOnCancel(values);
    },
  });

  const tabs = [
    { name: "General", isDisabled: false },
    { name: "Employee", isDisabled: false },
  ];

  const allTabs = useMemo(() => {
    const isTabDisabled = (tabName: string) => {
      tabName = tabName.toLowerCase();
      if (
        tabName === "admin" ||
        tabName === "employee" ||
        tabName === "general"
      )
        return false;
      return false;
    };
    return tabs.map((tab) => ({ ...tab, isDisabled: isTabDisabled(tab.name) }));
  }, []);
  const [activeTab, setActiveTab] = useState<{
    name: string;
    isDisabled: boolean;
  }>(allTabs[0]);

  useEffect(() => {
    if (openCategory) {
      const found = allTabs.find(
        (t) => t.name.toLowerCase() === openCategory.toLowerCase()
      );
      if (found && !found.isDisabled) setActiveTab(found);
    }
  }, [openCategory]);

  const [handleChanges, setHandleChanges] = useState<boolean>();

  useEffect(() => {
    const hasChanges = _.isEqual(grantLetterSettingDetails, formik.values);

    setHandleChanges(hasChanges);
  }, [initialSettings, grantLetterSettingDetails, formik.values]);

  const [grantLetter, setGrantLetter] = useState(
    formik.values.grantLetterTemplates
  );

  const [imageDialog, setImageDialog] = useState<{
    open: boolean;
  }>({ open: false });

  const [fileToUpload, setFileTopUpload] = useState("");
  const [fileName, setFileName] = useState("Upload File");
  const isValidImageFileUploaded = (
    file: File,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const validExtensions = ["png", "jpeg", "jpg"];
    const fileExtension = file.type.split("/")[1];
    if (!validExtensions.includes(fileExtension)) {
      return false;
    }
    if (file.size / 1024 > 50) {
      e.target.files = null;
      e.target.value = "";
      toast(
        `Your uploaded image size is ${(file.size / 1024).toFixed(
          4
        )}KB, File size should be less than 50kb, please upload a different file`,
        {
          type: "error",
        }
      );
      return false;
    } else return true;
  };
  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    if (!e?.target?.files?.[0]) return;
    const file = e.target.files[0];
    const base64 = (await convertToBase64(file)) as unknown as string;
    setFileTopUpload(base64);
    setFileName("File Uploaded");
    formik.setFieldValue("authorizedSignature", base64);
  }
  const downloadGrantLetterTemplate = async (
    s3Path: string,
    fileName = "GrantLetterTemplate.docx"
  ) => {
    const res = await fetch(s3Path);
    const blob = await res.blob();
    downloadBlobObject(blob, fileName);
  };
  const { mutate: deleteDefaultSignature, isLoading } =
    useDeleteDefaultSignature();
  function deleteSignature() {
    deleteDefaultSignature(
      {},
      {
        onSuccess: () => {
          toast("Deleted Successfully", { autoClose: 2000, type: "success" });
        },
        onError: (err: any) => {
          toast("Error", { autoClose: 2000, type: "error" });
        },
      }
    );
  }
  return (
    <form key="GrantLetterSettings" onSubmit={formik.handleSubmit}>
      <Box className="md:w-[425px] lg:w-[850px] p-4 bg-white rounded-lg">
        <Box className="">
          <VStack className="justify-between p-4 gap-9">
            <Box className="items-center mt-6 text-lg font-medium text-gray-dark">
              <SettingHeader
                title="Grant Settings"
                description="Configure your grant settings"
              />
            </Box>
            <HStack className="gap-8 mb-0 border-t-2 border-dotted flex-center">
              {allTabs.map((tab) => (
                <Box
                  onClick={() => {
                    if (!tab.isDisabled) setActiveTab(tab);
                  }}
                  key={tab.name}
                  className={`text-xs3 font-semibold py-4  ${
                    tab.isDisabled
                      ? "text-blue-400 cursor-not-allowed"
                      : "cursor-pointer"
                  } ${
                    tab.name === activeTab.name
                      ? "border-b-2 border-solid border-red-500 text-red-500"
                      : "text-gray-201 "
                  }`}
                >
                  {tab.name}
                </Box>
              ))}
            </HStack>
            <VStack className="p-4">
              {activeTab.name === "General" && (
                <GeneralEmailSettings formik={formik} />
              )}
              {activeTab.name === "Employee" && (
                <EmployeeGrantSettings formik={formik} />
              )}
            </VStack>
          </VStack>
        </Box>
        {!isUserEsopViewer && (
          <HStack className="justify-between mt-12">
            <ButtonPrimary1 type="reset" className="mr-8 text-red-500">
              Cancel
            </ButtonPrimary1>
            <ButtonPrimary
              className={`${
                !handleChanges ||
                !determineUserAccessToResource(
                  permission?.aclList || [],
                  Resource.CompanySettings,
                  DefaultAction.All
                )
                  ? " cursor-not-allowed"
                  : ""
              } flex items-center self-end justify-center`}
              disabled={
                isError ||
                handleChanges ||
                !determineUserAccessToResource(
                  permission?.aclList || [],
                  Resource.CompanySettings,
                  DefaultAction.All
                )
              }
              type="submit"
              loading={isSettingsSaved}
            >
              Save Settings
            </ButtonPrimary>
          </HStack>
        )}
      </Box>
    </form>
  );
}

const ActionIndicator = (props: {
  status: "ADDED" | "EDITED" | "DELETED" | undefined;
}): React.ReactElement => {
  const status = props.status;
  const colourMapper = {
    ADDED: "text-green-400",
    EDITED: "text-blue-400",
    DELETED: "text-red-400",
  };
  return (
    <span className={`pl-0.5 text-xxs ${colourMapper[status || "EDITED"]}`}>
      {" "}
      {status && `will be ${status.toLowerCase()}`}
    </span>
  );
};
const DateFormatSelector: React.FC<DateFormatSelectorProps> = ({
  initialOrder,
  separator = "-",
  onChange,
}) => {
  const [formats, setFormats] = useState(initialOrder);
  const [draggedItemIndex, setDraggedItemIndex] = useState<number | null>(null);
  useEffect(() => {
    setFormats(initialOrder);
  }, [initialOrder]);
  const availableFormats = {
    Day: ["d", "dd", "do"],
    Month: ["M", "Mo", "MM", "MMM", "MMMM"],
    Year: ["y", "yyy", "yyyy"],
  };

  const getLabel = (format: string) => {
    if (availableFormats.Day.includes(format)) return "Day";
    if (availableFormats.Month.includes(format)) return "Month";
    if (availableFormats.Year.includes(format)) return "Year";
    return "Unknown";
  };

  const getOptions = (format: string) => {
    const label = getLabel(format);
    if (label === "Unknown") return [];
    const availableValues = Array.from(new Set(availableFormats[label] || []));
    return availableValues;
  };

  const handleDragStart = (index: number) => {
    setDraggedItemIndex(index);
  };

  const handleDragOver = (index: number) => {
    if (draggedItemIndex === null || draggedItemIndex === index) return;

    const newFormats = [...formats];
    const draggedFormat = newFormats[draggedItemIndex];

    newFormats.splice(draggedItemIndex, 1);
    newFormats.splice(index, 0, draggedFormat);

    setFormats(newFormats);
    setDraggedItemIndex(index);

    onChange(newFormats);
  };

  const handleDragEnd = () => {
    setDraggedItemIndex(null);
  };

  const handleFormatChange = (index: number, newFormat: string) => {
    const newFormats = [...formats];
    newFormats[index] = newFormat;

    setFormats(newFormats);

    onChange(newFormats);
  };
  const getFlex = (label: string) => {
    const flex = "30%";
    if (label === "Month") return "40%";
    if (label === "Year") return "35%";
    return flex;
  };
  return (
    <div className="w-full">
      <div className="flex justify-between w-full">
        {formats.map((format, index) => (
          <div
            key={index}
            draggable
            onDragStart={() => handleDragStart(index)}
            onDragOver={(e) => {
              e.preventDefault();
              handleDragOver(index);
            }}
            onDragEnd={handleDragEnd}
            className={`flex-1 m-1 p-1 text-center rounded-md cursor-move transition-all duration-200 
              ${draggedItemIndex === index ? "shadow-lg" : ""} `}
            style={{
              flexBasis: getFlex(getLabel(format)),
            }}
          >
            <p className="flex gap-2 items-center justify-center">
              {getLabel(format)}{" "}
              <Icon
                className="text-end inline-flex"
                height={12}
                width={12}
                icon={"carbon:draggable"}
              ></Icon>{" "}
            </p>
            <Select
              isPlaceHolderDisabled={true}
              placeholder={getLabel(format)}
              options={getOptions(format)}
              value={format}
              onChange={(e) => handleFormatChange(index, e.target.value)}
            />
          </div>
        ))}
      </div>
      <div
        className="mt-2 ml-1 text-blue-400 transition-transform duration-300 ease-in-out transform scale-105"
        key={formats.join(separator)}
      >
        Example: {format(new Date(), formats.join(separator))}
      </div>
      {formats.join(separator)}
    </div>
  );
};

const GeneralEmailSettings: React.FC<SubSettingsProps> = ({ formik }) => {
  const { getFieldProps, values, touched, errors } = formik;
  const hasAcceptanceWindowChanged =
    Number(values.acceptanceNumber) !==
    Number(formik.initialValues.acceptanceNumber);
  const [imageResizerProps, setImageResizerProps] = useState<{
    open: boolean;
    imageUrl?: string;
  }>({ open: false, imageUrl: undefined });
  const [imageDialog, setImageDialog] = useState<{
    open: boolean;
  }>({ open: false });

  const downloadGrantLetterTemplate = async (
    s3Path: string,
    fileName = "GrantLetterTemplate.docx"
  ) => {
    const res = await fetch(s3Path);
    const blob = await res.blob();
    downloadBlobObject(blob, fileName);
  };

  const { mutate: deleteDefaultSignature, isLoading } =
    useDeleteDefaultSignature();
  function deleteSignature() {
    //
    deleteDefaultSignature(
      {},
      {
        onSuccess: () => {
          toast("Deleted Successfully", { autoClose: 2000, type: "success" });
          formik.setFieldValue("authorizedSignature", "");
        },
        onError: (err: any) => {
          toast("Error", { autoClose: 2000, type: "error" });
        },
      }
    );
  }

  const [fileToUpload, setFileTopUpload] = useState("");
  const [fileName, setFileName] = useState("Upload File");
  const isValidImageFileUploaded = (
    file: File,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const validExtensions = ["png", "jpeg", "jpg"];
    const fileExtension = file.type.split("/")[1];
    if (!validExtensions.includes(fileExtension)) {
      return false;
    }
    if (file.size / 1024 > 50) {
      e.target.files = null;
      e.target.value = "";
      toast(
        `Your uploaded image size is ${(file.size / 1024).toFixed(
          4
        )}KB, File size should be less than 50kb, please upload a different file`,
        {
          type: "error",
        }
      );
      return false;
    } else return true;
  };

  const lapsingDateOptions = [
    { label: "Resignation Date", value: "resignationDate" },
    { label: "Last working Day", value: "lastWorkingDay" },
  ];
  return (
    <>
      <HStack className="gap-10 pb-6">
        <VStack className="w-full gap-10">
          <VStack className="w-full gap-10">
            <VStack className="w-full gap-2">
              <SettingsRow
                id="grant-acceptanceNumber"
                label="Window for acceptance of Grant letter (in days)"
                description="Specify the number of days within which the grant letter should be accepted."
                type="input"
                inputType="number"
                name="acceptanceNumber"
                value={formik.values.acceptanceNumber}
                getFieldProps={formik.getFieldProps}
                error={formik.errors?.acceptanceNumber}
              />
              {hasAcceptanceWindowChanged && (
                <HStack className="w-full justify-end">
                  <HStack
                    role="alert"
                    className="w-72 items-start gap-2 rounded-md bg-amber-50 px-3 py-2 text-amber-700"
                  >
                    <Icon
                      icon="material-symbols:warning-outline-rounded"
                      className="mt-0.5 h-4 w-4 shrink-0"
                    />
                    <span className="text-xs leading-4">
                      The new acceptance window applies only to future grant
                      offers.
                    </span>
                  </HStack>
                </HStack>
              )}
            </VStack>

            <SettingsRow
              id="grant-requestApproverSignature"
              label="Request Approver Signature while approving a Grant"
              description={`If Enabled, the approver would have to sign while approving the grant`}
              type="toggle"
              name="requestApproverSignature"
              value={formik.values.requestApproverSignature}
              onToggle={() =>
                formik.setFieldValue(
                  "requestApproverSignature",
                  !formik.values.requestApproverSignature
                )
              }
            />

            <SettingsRow
              id="grant-autoPauseVestingOnResignation"
              label="Auto Pause Vesting on Resignation Date"
              description={`Grants' Vestings will be paused on the resignation date`}
              type="toggle"
              name="autoPauseVestingOnResignation"
              value={formik.values.autoPauseVestingOnResignation || false}
              onToggle={() =>
                formik.setFieldValue(
                  "autoPauseVestingOnResignation",
                  !formik.values.autoPauseVestingOnResignation
                )
              }
            />
            <SettingsRow
              id="plan-doc-autoGenerate"
              label="Auto Generate Plan Document"
              description={`Enable this to automatically generate plan documents when plans are created or updated. If disabled, plan documents will need to be uploaded manually.`}
              type="toggle"
              name="generatePlanDocument"
              value={formik.values.generatePlanDocument ?? false}
              onToggle={() =>
                formik.setFieldValue(
                  "generatePlanDocument",
                  !formik.values.generatePlanDocument
                )
              }
            />
            <SettingsRow
              id="grant-showZeroVestingInGrantLetter"
              label="Show Zero Vesting in Grant Letter"
              description="Specify whether to show zero vesting in the grant letter."
              type="toggle"
              onToggle={() =>
                formik.setFieldValue(
                  "showZeroVestingInGrantLetter",
                  !formik.values.showZeroVestingInGrantLetter
                )
              }
              name="showZeroVestingInGrantLetter"
              value={formik.values.showZeroVestingInGrantLetter ?? false}
              getFieldProps={formik.getFieldProps}
              error={formik.errors?.showZeroVestingInGrantLetter}
            />
            <HStack>
              <SettingsRow
                id="grant-showValueAtGrantToAdmin"
                label="Show Value at Grant to Admins"
                description="Display the Value at Grant in the Grant Details table"
                type="toggle"
                onToggle={() =>
                  formik.setFieldValue(
                    "showValueAtGrantToAdmin",
                    !formik.values.showValueAtGrantToAdmin
                  )
                }
                name="showValueAtGrantToAdmin"
                value={formik.values.showValueAtGrantToAdmin ?? false}
                getFieldProps={formik.getFieldProps}
                error={formik.errors?.showValueAtGrantToAdmin}
                tooltip="Value at Grant is based on the share price from the day before the grant date. If it is a trading day, the average of the high and close price is used. If it is a holiday, the closing price of the most recent trading day is used."
              />
            </HStack>
            <SettingsRow
              id="grant-enableAutoExerciseRequest"
              label="Auto-Generate Exercise Request on RSU Vesting"
              description={`Enable this to automatically create an Exercise Request whenever RSUs vest. Disable it to require manual initiation.`}
              type="toggle"
              name="enableAutoExerciseRequest"
              value={formik.values.enableAutoExerciseRequest ?? true}
              onToggle={() =>
                formik.setFieldValue(
                  "enableAutoExerciseRequest",
                  !formik.values.enableAutoExerciseRequest
                )
              }
            />

            <SettingsRow
              id="grant-discardOfferedGrantsOnResignation"
              label="Delete Offered Grants upon Completing Resignation"
              description={`This action will impact only for grants in OFFERED state, grants with states other than GRANTED and OFFERED will be deleted regardless of the toggle value`}
              type="toggle"
              name="discardOfferedGrantsOnResignation"
              value={formik.values.discardOfferedGrantsOnResignation || false}
              onToggle={() =>
                formik.setFieldValue(
                  "discardOfferedGrantsOnResignation",
                  !formik.values.discardOfferedGrantsOnResignation
                )
              }
            />

            <HStack
              className="w-full justify-between gap-6"
              id="grant-lapsingDateOptions"
            >
              <VStack className="">
                <span className="font-medium">
                  Date to use for Grant Lapsing
                </span>
                <span className="text-xs text-gray-500">
                  Date to use for Grant Lapsing post resignation
                </span>
              </VStack>
              <Box className="flex items-end ml-2">
                <Select
                  className="w-30 font-medium text-sm"
                  options={lapsingDateOptions}
                  value={formik.values?.dateForLapsing}
                  onChange={(event) => {
                    formik.setFieldValue("dateForLapsing", event.target.value);
                  }}
                  textGetter={(option) => option.label}
                  valueGetter={(option) => option.value}
                />
              </Box>
            </HStack>
            <HStack className="justify-between w-full">
              <VStack className="">
                <span className="font-medium">
                  Default authorized signature to be used on Grant letter
                </span>
                <span className="text-xs text-gray-500">
                  Authorized signature to be used on grant letter upon approval
                </span>
              </VStack>
              <HStack className="flex gap-4">
                {!!formik.values.authorizedSignature && (
                  <Box
                    className="underline cursor-pointer text-orange-501"
                    onClick={() => setImageDialog({ open: true })}
                  >
                    Authorized Signature
                  </Box>
                )}
                <ButtonPrimary1
                  onClick={() =>
                    setImageResizerProps({ open: true, imageUrl: undefined })
                  }
                >
                  Upload new Signature
                </ButtonPrimary1>
                {imageResizerProps.open && (
                  <Dialog
                    open={imageResizerProps.open}
                    onClose={() => setImageResizerProps({ open: false })}
                    maxWidth="lg"
                  >
                    <ImageResizer
                      onCancel={() => setImageResizerProps({ open: false })}
                      imageUrl={imageResizerProps.imageUrl}
                      imageConditions={{
                        width: 240,
                        height: 120,
                      }}
                      onSave={(base64Image?: string) => {
                        if (!base64Image) {
                          return;
                        }
                        const img = new Image();
                        img.src = base64Image;
                        img.onload = async () => {
                          const width = img.width;
                          const height = img.height;
                          if (width > 240 || height > 120) {
                            toast(
                              "File height and width should be less than 120px and 240px",
                              {
                                type: "error",
                              }
                            );
                            formik.setFieldValue("authorizedSignature", "");
                            setFileTopUpload("");
                            setFileName("upload file");
                            return;
                          }
                          setFileTopUpload(base64Image);
                          setFileName("File Uploaded");
                          formik.setFieldValue(
                            "authorizedSignature",
                            base64Image
                          );
                          setImageResizerProps({
                            open: false,
                            imageUrl: base64Image,
                          });
                        };
                      }}
                    />
                  </Dialog>
                )}
                <Dialog
                  open={imageDialog.open}
                  onClose={() => setImageDialog({ open: false })}
                  maxWidth="lg"
                >
                  {
                    <img
                      className="image"
                      src={formik.values.authorizedSignature}
                      alt="no image"
                    />
                  }
                </Dialog>
                {!!formik.values.authorizedSignature && (
                  <Icon
                    icon={"mdi:delete-empty"}
                    className="text-orange-501"
                    width={24}
                    onClick={() => deleteSignature()}
                  />
                )}
              </HStack>
            </HStack>
            <SettingsRow
              id="grant-showActiveGrantedOptionsToAdmin"
              label="Show Active granted options to Admin"
              description="If enabled, active options of grant (active vested + unvested) will be shown to admin including withdrawn options without lapse."
              type="toggle"
              name="showActiveGrantedOptionsToAdmin"
              value={formik.values.showActiveGrantedOptionsToAdmin ?? false}
              onToggle={() =>
                formik.setFieldValue(
                  "showActiveGrantedOptionsToAdmin",
                  !formik.values.showActiveGrantedOptionsToAdmin
                )
              }
            />
            <SettingsRow
              id="grant-disableAutoGrantLetterGeneration"
              label="Disable Auto Grant Letter Generation"
              description="If enabled, grant letters will not be automatically generated upon single or bulk grant creation."
              type="toggle"
              name="disableAutoGrantLetterGeneration"
              value={formik.values.disableAutoGrantLetterGeneration ?? false}
              onToggle={() =>
                formik.setFieldValue(
                  "disableAutoGrantLetterGeneration",
                  !formik.values.disableAutoGrantLetterGeneration
                )
              }
            />
          </VStack>
        </VStack>
      </HStack>
    </>
  );
};

const EmployeeGrantSettings: React.FC<SubSettingsProps> = ({ formik }) => {
  const { getFieldProps, values, touched, errors } = formik;
  return (
    <>
      <HStack className="gap-10 pb-6">
        <VStack className="w-full gap-10">
          <SettingsRow
            id="grant-employeeSignatureForGrant"
            label="Request Employees to Sign the Grant Letter"
            description={`If Enabled, please make sure that Grant Letter Template has the placeholder
              "IMAGE insertImage(Image_EmployeeSignatory)" for Employee Signature`}
            type="toggle"
            name="employeeSignatureForGrant"
            value={formik.values.employeeSignatureForGrant}
            onToggle={() =>
              formik.setFieldValue(
                "employeeSignatureForGrant",
                !formik.values.employeeSignatureForGrant
              )
            }
          />
          <VStack className="w-full gap-3">
            <SettingsRow
              id="grant-showGrantAcceptanceDisclaimer"
              label="Show Disclaimer Before Accepting/Signing Grant Letter"
              description="If Enabled, employees must review the grant letter (or grant details, if no letter is generated) and check a disclaimer checkbox before they can accept or sign the grant."
              type="toggle"
              name="showGrantAcceptanceDisclaimer"
              value={formik.values.showGrantAcceptanceDisclaimer ?? false}
              onToggle={() =>
                formik.setFieldValue(
                  "showGrantAcceptanceDisclaimer",
                  !formik.values.showGrantAcceptanceDisclaimer
                )
              }
            />
            {formik.values.showGrantAcceptanceDisclaimer && (
              <VStack
                className="w-full gap-2 pl-2"
                id="grant-grantAcceptanceDisclaimerText"
              >
                <Label>Disclaimer Text</Label>
                <TextArea
                  className="w-full min-h-[100px]"
                  name="grantAcceptanceDisclaimerText"
                  value={formik.values.grantAcceptanceDisclaimerText || ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="e.g. I confirm that I have read and understood the terms of this grant letter."
                />
                {formik.touched.grantAcceptanceDisclaimerText &&
                  formik.errors.grantAcceptanceDisclaimerText && (
                    <span className="text-xs text-red-500">
                      {formik.errors.grantAcceptanceDisclaimerText}
                    </span>
                  )}
              </VStack>
            )}
          </VStack>
        </VStack>
      </HStack>
    </>
  );
};

export default GrantLetterSettings;
